import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { useCart } from '../../../contexts/CartContext';
import { toast } from 'react-hot-toast';

const SaleProductCard = ({ product, index, onProductClick }) => {
  const { addToCart, cartItems } = useCart();
  const [isWishlisted, setIsWishlisted] = useState(product?.isWishlisted || false);
  const [isHovered, setIsHovered] = useState(false);
  const [addingProductId, setAddingProductId] = useState(null);
  
  // Vérifier si le produit est déjà dans le panier
  const isInCart = (productId) => {
    return cartItems.some(item => item.id === productId);
  };

  const discountPercentage = Math.round(((product?.originalPrice - product?.salePrice) / product?.originalPrice) * 100);
  const savings = product?.originalPrice - product?.salePrice;

  const handleWishlistToggle = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsWishlisted(!isWishlisted);
  };

  const handleQuickAdd = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    handleAddToCart(e);
  };

  const handleAddToCart = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (isInCart(product.id) || addingProductId === product.id) return;
    setAddingProductId(product.id);
    
    try {
      await new Promise((resolve) => {
        addToCart({
          id: product.id,
          name: product.name,
          price: parseFloat(product.salePrice),
          image: product.image,
          size: product.availableSizes?.[0] || 'Unique',
          color: 'Standard',
          source: 'From Sale'
        });
        
        // Afficher une notification de succès
        toast.success('Produit ajouté au panier', {
          position: 'top-center',
          duration: 2000,
          style: {
            background: '#10B981',
            color: '#fff',
            padding: '12px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
          icon: '✅',
        });
        
        // Attendre 1,5 seconde avant de réinitialiser l'état de chargement
        setTimeout(() => {
          resolve();
        }, 1500);
      });
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      toast.error('Erreur lors de l\'ajout au panier', {
        position: 'top-center',
        duration: 2000,
      });
    } finally {
      setAddingProductId(null);
    }
  };

  const getBadgeColor = () => {
    if (discountPercentage >= 70) return 'bg-error text-error-foreground';
    if (discountPercentage >= 50) return 'bg-warning text-warning-foreground';
    if (discountPercentage >= 30) return 'bg-accent text-accent-foreground';
    return 'bg-success text-success-foreground';
  };

  const getUrgencyLevel = () => {
    if (product?.stock <= 3) return 'critical';
    if (product?.stock <= 10) return 'low';
    return 'normal';
  };

  const urgencyLevel = getUrgencyLevel();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -5 }}
      className="group relative bg-card border border-street rounded-lg overflow-hidden hover:border-accent transition-street box-shadow-street hover:box-shadow-modal cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onProductClick(product)}
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden">
        <Image
          src={product?.image}
          alt={product?.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-street duration-500"
        />
        
        {/* Discount Badge */}
        <div className={`absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-bold ${getBadgeColor()} animate-pulse-glow`}>
          -{discountPercentage}%
        </div>

        {/* Special Badges */}
        <div className="absolute top-3 right-3 flex flex-col space-y-1">
          {product?.isFlashSale && (
            <div className="bg-error text-error-foreground px-2 py-1 rounded-full text-xs font-bold animate-pulse">
              FLASH
            </div>
          )}
          {product?.isLimitedEdition && (
            <div className="bg-warning text-warning-foreground px-2 py-1 rounded-full text-xs font-bold">
              LIMITED
            </div>
          )}
          {product?.isClearance && (
            <div className="bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-bold">
              FINAL SALE
            </div>
          )}
        </div>

        {/* Wishlist Button */}
        <button
          onClick={handleWishlistToggle}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-street ${
            isWishlisted 
              ? 'bg-error text-error-foreground' 
              : 'bg-background/80 text-foreground hover:bg-accent hover:text-accent-foreground'
          }`}
          style={{ marginTop: product?.isFlashSale || product?.isLimitedEdition || product?.isClearance ? '2.5rem' : '0' }}
        >
          <Icon name={isWishlisted ? "Heart" : "Heart"} size={16} className={isWishlisted ? 'fill-current' : ''} />
        </button>

        {/* Hover Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          className="absolute inset-0 bg-background/80 flex items-center justify-center"
        >
          <div className="text-center space-y-3">
            
            <button
              onClick={handleQuickAdd}
              className="bg-accent text-accent-foreground px-4 py-2 rounded-lg font-medium hover:scale-105 transition-street flex items-center justify-center w-full disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!product.stock || product.stock <= 0 || addingProductId === product.id}
            >
              {addingProductId === product.id ? (
                <>
                  <Icon name="Loader" className="animate-spin mr-2" size={16} />
                  Ajout...
                </>
              ) : isInCart(product.id) ? (
                <>
                  
                  Déjà au panier
                </>
              ) : (
                <>
                  <Icon name="ShoppingBag" className="mr-2" size={16} />
                  {product.stock > 0 ? 'Ajouter au panier' : 'Rupture de stock'}
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Product Info */}
      <div className="p-4">
        {/* Brand & Name */}
        <div className="mb-2">
          <p className="text-xs text-accent font-medium uppercase tracking-wide">
            {product?.brand}
          </p>
          <h3 className="text-sm font-medium text-foreground truncate group-hover:text-accent transition-street">
            {product?.name}
          </h3>
        </div>

        {/* Pricing, Stock, and Rating */}
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 mb-2">
          {/* Pricing */}
          <div className="flex items-baseline space-x-2">
            <div>
              <div className="text-lg font-bold text-accent">
                {product?.salePrice?.toFixed(3)}
              </div>
              <div className="text-accent text-sm font-medium">TND</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground line-through">
                {product?.originalPrice?.toFixed(3)}
              </div>
              <div className="text-xs text-muted-foreground line-through">TND</div>
            </div>
          </div>

          {/* Stock Status */}
          <div className="flex items-center space-x-1">
            <Icon 
              name="Package" 
              size={14} 
              className={urgencyLevel === 'critical' ? 'text-error' : urgencyLevel === 'low' ? 'text-warning' : 'text-success'} 
            />
            <span className={`text-xs font-medium ${
              urgencyLevel === 'critical' ? 'text-error' : urgencyLevel === 'low' ? 'text-warning' : 'text-success'
            }`}>
              {product?.stock > 0 ? `${product?.stock} restant(s)` : 'Rupture de stock'}
            </span>
          </div>

          {/* Rating */}
          <div className="flex items-center space-x-1">
            <Icon name="Star" size={12} className="text-warning fill-current" />
            <span className="text-xs text-muted-foreground">
              {product?.rating} ({product?.reviews})
            </span>
          </div>
        </div>

        {/* Bouton Ajouter au panier - Version mobile/visible par défaut */}
        <div className="lg:hidden mt-3">
          <button
            onClick={handleAddToCart}
            className="w-full bg-accent text-accent-foreground py-2 px-4 rounded-lg font-medium hover:bg-accent/90 transition-street flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!product.stock || product.stock <= 0 || addingProductId === product.id}
          >
            {addingProductId === product.id ? (
              <>
                <Icon name="Loader" className="animate-spin mr-2" size={16} />
                Ajout...
              </>
            ) : isInCart(product.id) ? (
              <>
                
                Déjà au panier
              </>
            ) : (
              <>
                <Icon name="ShoppingBag" className="mr-2" size={16} />
                {product.stock > 0 ? 'Ajouter au panier' : 'Rupture de stock'}
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Bouton Ajouter au panier - Version desktop (visible au survol) */}
      <div className="hidden lg:block absolute bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur-sm translate-y-full group-hover:translate-y-0 transition-transform duration-300 border-t border-street">
        <button
          onClick={handleAddToCart}
          className="w-full bg-accent text-accent-foreground py-2 px-4 rounded-lg font-medium hover:bg-accent/90 transition-street flex items-center justify-center"
          disabled={!product.stock || product.stock <= 0}
        >
          <Icon name="ShoppingBag" className="mr-2" size={16} />
          {product.stock > 0 ? 'Ajouter au panier' : 'Rupture de stock'}
        </button>
      </div>
    </motion.div>
  );
};

export default SaleProductCard;